import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNav } from "../../components/NavContext";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const MapPicker = ({ position, onChange }) => {
  useMapEvents({
    click(e) {
      onChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return position ? <Marker position={position} /> : null;
};

const LocationEditor = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationTypes, setLocationTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [coordMode, setCoordMode] = useState("map"); // "map" | "manual"
  const mapRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    title: "",
    sub_title: "",
    location_type_id: "",
    keywords: "",
    search_text: "",
    latitude: "",
    longitude: "",
  });

  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const API_URL = process.env.REACT_APP_API_URL;
  const { addLink } = useNav();

  useEffect(() => {
    addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
    addLink("Location Editor", `${DOMAIN}control/admin/location-editor`);
  }, [addLink, DOMAIN]);

  useEffect(() => {
    document.title = "Location Editor";
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}location-types`).then((res) => {
      setLocationTypes(res.data?.data || res.data || []);
    }).catch(() => {});
  }, [API_URL]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const res = await axios.get(`${API_URL}locations`, {
        params: { name: searchQuery.trim(), per_page: 10 },
        withCredentials: true,
      });
      setSearchResults(res.data?.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [API_URL, searchQuery]);

  const handleSelect = (loc) => {
    setSelectedLocation(loc);
    setForm({
      name: loc.name || "",
      title: loc.title || "",
      sub_title: loc.sub_title || "",
      location_type_id: loc.location_type_id || "",
      keywords: loc.keywords || "",
      search_text: loc.search_text || "",
      latitude: loc.latitude || "",
      longitude: loc.longitude || "",
    });
    setSearchResults([]);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleMapClick = (lat, lng) => {
    setForm((f) => ({ ...f, latitude: lat.toFixed(8), longitude: lng.toFixed(8) }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedLocation) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await axios.put(`${API_URL}locations/${selectedLocation.id}`, {
        name: form.name,
        title: form.title,
        sub_title: form.sub_title,
        location_type_id: form.location_type_id || undefined,
        keywords: form.keywords,
        search_text: form.search_text,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
      }, { withCredentials: true });
      setSaveSuccess(true);
      setSelectedLocation((prev) => ({ ...prev, ...form }));
    } catch (e) {
      setSaveError(e.response?.data?.message || e.response?.data?.error || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const mapPosition = form.latitude && form.longitude
    ? [parseFloat(form.latitude), parseFloat(form.longitude)]
    : [33.3152, 44.3661]; // Baghdad default

  return (
    <div className="container-fluid mt-4" dir="rtl">
      <header className="mb-4 text-center">
        <h1 className="display-6 mb-1">تعديل الموقع</h1>
        <p className="text-muted mb-0">ابحث عن موقع وعدّل بياناته</p>
      </header>

      {/* Search */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="ابحث باسم الموقع..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              dir="rtl"
            />
            <button className="btn btn-primary" onClick={handleSearch} disabled={searchLoading}>
              {searchLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                "بحث"
              )}
            </button>
          </div>

          {searchResults.length > 0 && (
            <ul className="list-group mt-2">
              {searchResults.map((loc) => (
                <li
                  key={loc.id}
                  className="list-group-item list-group-item-action"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSelect(loc)}
                >
                  <span className="badge bg-secondary me-2">#{loc.id}</span>
                  {loc.name}
                  {loc.sub_title && <small className="text-muted ms-2">— {loc.sub_title}</small>}
                </li>
              ))}
            </ul>
          )}
          {!searchLoading && searchResults.length === 0 && searchQuery && (
            <p className="text-muted mt-2 mb-0 small">لا نتائج</p>
          )}
        </div>
      </div>

      {/* Editor */}
      {selectedLocation && (
        <div className="card shadow-sm">
          <div className="card-header">
            <strong>تعديل: {selectedLocation.name}</strong>
            <small className="text-muted ms-2">ID: {selectedLocation.id}</small>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">الاسم <span className="text-danger">*</span></label>
                <input type="text" className="form-control" name="name" value={form.name} onChange={handleFormChange} dir="rtl" />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">العنوان (title)</label>
                <input type="text" className="form-control" name="title" value={form.title} onChange={handleFormChange} dir="rtl" />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">العنوان الفرعي (sub_title)</label>
                <input type="text" className="form-control" name="sub_title" value={form.sub_title} onChange={handleFormChange} dir="rtl" />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">نوع الموقع</label>
                <select className="form-select" name="location_type_id" value={form.location_type_id} onChange={handleFormChange}>
                  <option value="">-- اختر النوع --</option>
                  {locationTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>{lt.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label fw-bold">الكلمات المفتاحية (keywords)</label>
                <textarea className="form-control" name="keywords" rows={2} value={form.keywords} onChange={handleFormChange} dir="rtl" />
              </div>
              <div className="col-12">
                <label className="form-label fw-bold">نص البحث (search_text)</label>
                <textarea className="form-control" name="search_text" rows={2} value={form.search_text} onChange={handleFormChange} dir="rtl" />
                <small className="text-muted">يُعاد حساب هذا الحقل تلقائياً عند الحفظ</small>
              </div>

              {/* Coordinates */}
              <div className="col-12">
                <label className="form-label fw-bold">الموقع الجغرافي</label>
                <div className="btn-group mb-3 d-flex" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${coordMode === "map" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setCoordMode("map")}
                  >
                    تحديد على الخريطة
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${coordMode === "manual" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setCoordMode("manual")}
                  >
                    إدخال يدوي
                  </button>
                </div>

                {coordMode === "map" && (
                  <div style={{ height: 350, borderRadius: 8, overflow: "hidden" }}>
                    <MapContainer
                      center={mapPosition}
                      zoom={15}
                      style={{ height: "100%", width: "100%" }}
                      ref={mapRef}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <MapPicker
                        position={form.latitude && form.longitude ? mapPosition : null}
                        onChange={handleMapClick}
                      />
                    </MapContainer>
                    <small className="text-muted d-block mt-1">انقر على الخريطة لتحديد الموقع</small>
                  </div>
                )}

                {coordMode === "manual" && (
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label">خط العرض (latitude)</label>
                      <input
                        type="number"
                        step="any"
                        className="form-control font-monospace"
                        name="latitude"
                        value={form.latitude}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">خط الطول (longitude)</label>
                      <input
                        type="number"
                        step="any"
                        className="form-control font-monospace"
                        name="longitude"
                        value={form.longitude}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>
                )}

                {form.latitude && form.longitude && (
                  <div className="mt-2">
                    <small className="text-muted font-monospace">
                      {parseFloat(form.latitude).toFixed(6)}, {parseFloat(form.longitude).toFixed(6)}
                    </small>
                  </div>
                )}
              </div>
            </div>

            {saveError && (
              <div className="alert alert-danger mt-3">{saveError}</div>
            )}
            {saveSuccess && (
              <div className="alert alert-success mt-3">تم الحفظ بنجاح</div>
            )}

            <div className="mt-4">
              <button
                className="btn btn-success w-100"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    جاري الحفظ...
                  </>
                ) : (
                  "حفظ التغييرات"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationEditor;
